/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosRequestConfig } from 'axios';

export interface ConfigType {
  ApiUrl: string;
}

// Dashboard

export interface IGetValuationScalarOpinionsResponse {
  id: number;
  report_date: ReportDate;
  weighted_market_enterprise_value: string;
  weighted_equity_value: string;
  less_debt: string;
  marketable_value_per_common_share: string;
  discount_for_lack_of_marketability: string;
  non_marketable_value_per_common_share: string;
  approved_for_clients: boolean;
  valuation_report?: string | null;
}

export interface IFindValuationScalarOpinionResponse extends IGetValuationScalarOpinionsResponse {
  approaches: Approach[];
}

export interface Approach {
  id: number;
  weight: number;
  name: string;
  is_deleted: boolean;
  deleted_at: string;
  approach_type: number;
  equity_value: string;
  enterprise_value: string;
  financial_statement_id: null;
  valuation: number;
}

export interface ReportDate {
  id: number;
  measurement_date: MeasurementDate;
  created_at: string;
  updated_at: string;
  has_final_allocation: boolean;
  company: number;
}

export interface MeasurementDate {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  slug: string;
  is_deleted: boolean;
  deleted_at: string;
  date: string;
  is_open: boolean;
}

// Captable

export interface IGetWaterfallBreakpointResponse {
  waterfall_breakpoints: WaterfallBreakpoints;
  use_custom_breakpoints: boolean;
  custom_breakpoints: null;
  is_waterfall_visible: boolean;
}

export interface IGetCaptableByCompanyRequest {
  mp: number;
}

export interface IGetCaptableByCompanyResponse {
  id: number;
  name: string;
  slug: string;
  is_primary: boolean;
  is_empty: boolean;
}

export interface IGetCaptableResponse {
  id: number;
  securities: Security[];
  fund_ownership: null;
  created_at: string;
  updated_at: string;
  name: string;
  slug: string;
  shares_outstanding: string;
  shares_diluted: string;
  current_own_percent: string;
  diluted_own_percent: string;
  initial_liquidation_preference: string;
  is_primary: boolean;
  waterfall_breakpoints: WaterfallBreakpoints;
  incremental_distribution: null;
  distribution_percent: null;
  use_custom_breakpoints: boolean;
  custom_breakpoints: null;
  company_measurement_date: number;
}

export interface Security {
  id: number;
  convertible_notes: any[];
  created_at: string;
  updated_at: string;
  name: string;
  is_deleted: boolean;
  deleted_at: string;
  investment_date: string | null;
  issue_price: string;
  shares_outstanding: string;
  conversion_rate: string;
  pik_shares: null;
  shares_diluted: string;
  current_own_percent: string;
  diluted_own_percent: string;
  strike_price: string;
  liquidation_preference: string;
  seniority: number;
  is_participating: boolean;
  is_participation_cap: boolean;
  participation_cap: string;
  has_dividends: boolean;
  has_multiple_investments: null;
  dividend_payment_type: null;
  dividend_rate: string;
  has_compounding: boolean;
  compounding_period: string;
  accrued_dividends: null;
  initial_liquidation_pref: string;
  total_preference: string;
  multiple_investments: null;
  order: number;
  options_ledger: null;
  security_ref: string;
  underlying_security_ref: string | null;
  security_group_ref: null;
  is_hidden: boolean;
  total_note_preference: null;
  total_note_value_at_md: null;
  cap_table: number;
  security_type: number;
  underlying_security: number | null;
}

export interface WaterfallBreakpoints {
  breakpoints: Breakpoint[];
  prices_per_share: number[] | string[];
}

export interface Breakpoint {
  id: number;
  name: string;
  price: number | string;
  range: string;
  total: number | string;
  series: Series[];
}

export interface Series {
  id: number;
  value: number | string | null;
  security: string;
}

// auth

export interface IRefreshAccessTokenRequest {
  refresh: string;
}

export interface IRefreshAccessTokenResponse {
  access: string;
}

export interface IGetAccessTokensRequest {
  username: string;
  password: string;
}

export interface IGetAccessTokensResponse {
  access: string;
  refresh: string;
}

export interface ISendAccessTokenRequest {
  credential: string;
  provider: string;
}

export interface ISendAccessTokenResponse {
  access: string;
  refresh: string;
  user: unknown;
}

export interface IVerifyAccessTokenRequest {
  token: string;
}

export interface IInterceptorConfigRequest extends AxiosRequestConfig {
  sent?: boolean;
  throttleRetryCount?: number;
}

export interface IGetGpcCompsResponse {
  value_over_time: HistoricalData[];
  rate_of_change: HistoricalData[];
  current_value: ComparisonCompany[];
  medians: Medians;
}

export type MetricValue = {
  value: number;
  growth_percentage: number;
};

export interface Medians {
  ntm_revenue_multiple: MetricValue;
  ntm_revenue_growth: MetricValue;
  gross_margin: MetricValue;
  ltm_revenue_multiple: MetricValue;
  ltm_revenue_growth: MetricValue;
  ebitda_margin: MetricValue;
}
export interface ComparisonCompany {
  name: string;
  ticker_symbol: string;
  enterprise_value: number;
  ltm_revenue: number;
  ntm_revenue: number;
  ev_divided_by_ltm_revenue: number;
  ev_divided_by_ntm_revenue: number;
  ltm_ebitda: number;
  ntm_ebitda: number;
  gross_margin: number;
  ebitda_margin: number;
  tangible_book_value: number;
  net_income: number;
  ntm_revenue_growth: number;
  ltm_revenue_growth: number;
}

export interface HistoricalData {
  month_6: TimeWindows[];
  cap_iq_id: string;
  ticker_symbol: string;
  month_3: TimeWindows[];
  year_10: TimeWindows[];
  year_5: TimeWindows[];
  year_1: TimeWindows[];
  year_3: TimeWindows[];
  color?: string;
}

export interface TimeWindows {
  dt: string;
  enterprise_value: number;
  gross_margin: number;
  ev_divided_by_ltm_revenue: number;
  ev_divided_by_ntm_revenue: number;
  ev_divided_by_ltm_ebitda: number;
  ev_divided_by_ntm_ebitda: number;
}

// Company
export interface IGetCompanyDetailsResponse {
  id: number;
  firm: number;
  name: string;
  slug: string;
  legal_company_name: null;
  website: string | null;
  fiscal_year_end: IFiscalYearEnd;
  captable_currency: string;
  financials_currency: string;
  address: string | null;
  phone_number: string | null;
  logo_url: string | null;
  icon_url: string | null;
  is_deleted: boolean;
  is_standalone: boolean;
  podio_id: number;
}

export interface IFiscalYearEnd {
  id: number;
  created_at: Date;
  updated_at: Date;
  end_month: number;
  end_day: number;
}

type TAddress = {
  city: string | null;
  country: string | null;
  line1: string | null;
  line2: string | null;
  postal_code: string | number | null;
  state: string | null;
};

export type TBillingDetails = {
  address: TAddress;
  email: string | null;
  name: string | null;
  phone: string | null;
};

export type TCard = {
  brand: string;
  checks: {
    address_line1_check: string | null;
    address_postal_code_check: string | null;
    cvc_check: string;
  };
  country: string;
  exp_month: number;
  exp_year: number;
  fingerprint: string;
  funding: string;
  generated_from: string | number | null;
  last4: string;
  networks: {
    available: string[];
    preferred: any;
  };
  three_d_secure_usage: {
    supported: boolean;
  };
};

export type Payload = {
  id: string;
  object: string;
  billing_details: TBillingDetails;
  card: TCard;
  wallet: boolean | null;
  created: number;
  customer: string | null;
  livemode: boolean;
  metadata: {
    order_id: string;
  };
  redaction: string | null;
  type: string;
};

export interface IGetPaymentMethodList {
  results?: Payload[];
}

export interface IGetSubscription {
  id: number;
  period: string;
  currency: string;
  price: string;
  auto_renewal: boolean;
  customer_id: string;
  active_payment_method: null;
  start_date: string;
  is_active: boolean;
  retry_attempts: number;
  retry_date: null;
  company: number;
}

export enum CompsDataType {
  VALUE_OVER_TIME = 'value_over_time',
  RATE_OF_CHANGE = 'rate_of_change',
  CURRENT_AND_MEDIANS = 'current_and_medians',
}
